import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Paperclip, Download, Upload, Trash2, FileText, Image as ImageIcon,
  Music, Video as VideoIcon, X, Loader2, ExternalLink,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes, i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function iconFor(ct) {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("audio/")) return Music;
  if (ct.startsWith("video/")) return VideoIcon;
  return FileText;
}

function tokenFromStorage() {
  try { return localStorage.getItem("hah_token") || ""; } catch { return ""; }
}

function fullUrl(pathOrUrl) {
  if (!pathOrUrl) return pathOrUrl;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function withAuth(url) {
  const token = tokenFromStorage();
  if (!token) return url;
  return `${url}${url.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}`;
}

function PreviewModal({ open, onClose, att }) {
  if (!att) return null;
  const ct = (att.content_type || att.mime || "").toLowerCase();
  const src = withAuth(fullUrl(att.download_url || `/api/uploads/${att.id}/download`));
  const legacy = att.url && !att.download_url ? fullUrl(att.url) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto p-0" data-testid="attachment-preview-modal">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="text-base flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-[#0B7E8C]" /> {att.filename}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#9CA3AF]">
            {ct || "unknown type"} · {humanSize(att.size_bytes || att.size)}
            {att.note ? ` · ${att.note}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 pb-4">
          {legacy ? (
            <a href={legacy} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[#0B7E8C] underline text-sm"
               data-testid="attachment-preview-legacy">
              Open external link <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : ct.startsWith("image/") ? (
            <img src={src} alt={att.filename} className="max-w-full rounded border border-[#E5E7EB]"
                 data-testid="attachment-preview-image" />
          ) : ct.startsWith("audio/") ? (
            <audio controls src={src} className="w-full" data-testid="attachment-preview-audio" />
          ) : ct.startsWith("video/") ? (
            <video controls src={src} className="max-w-full rounded" data-testid="attachment-preview-video" />
          ) : ct === "application/pdf" ? (
            <iframe title={att.filename} src={src} className="w-full h-[70vh] rounded border border-[#E5E7EB]"
                    data-testid="attachment-preview-pdf" />
          ) : (
            <div className="text-sm text-[#6B7280]" data-testid="attachment-preview-generic">
              No inline preview for this type.
              <a href={src} className="ms-2 text-[#0B7E8C] underline inline-flex items-center gap-1" download>
                Download <Download className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reusable attachments panel.
 *
 * Props:
 *   entityLabel:  string     shown in the empty-state
 *   listUrl:      string     GET endpoint returning {items: []}
 *   uploadUrl:    string     POST multipart endpoint (optional; omit to hide upload UI)
 *   deleteUrlFor: (att) => string | null   returns DELETE endpoint or null if not allowed
 *   canUpload:    boolean    whether to show the upload button (default: !!uploadUrl)
 *   testIdPrefix: string     prefix for data-testids (default 'attachments')
 *   compact:      boolean    render a compact chip list (for embedded cards)
 */
export default function AttachmentsPanel({
  entityLabel = "this record",
  listUrl,
  uploadUrl,
  deleteUrlFor,
  canUpload,
  testIdPrefix = "attachments",
  compact = false,
}) {
  const [items, setItems] = useState(null);
  const [previewAtt, setPreviewAtt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);

  const showUpload = canUpload ?? !!uploadUrl;

  const load = useCallback(async () => {
    if (!listUrl) return;
    try {
      const r = await api.get(listUrl);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    }
  }, [listUrl]);
  useEffect(() => { load(); }, [load]);

  async function onPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File exceeds 20 MB limit.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onDelete(att) {
    const url = deleteUrlFor?.(att);
    if (!url) return;
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    setDeletingId(att.id);
    try {
      await api.delete(url);
      toast.success("Attachment deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDeletingId(null); }
  }

  const list = useMemo(() => items || [], [items]);

  return (
    <div data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5" /> Documents ({items === null ? "…" : list.length})
        </div>
        {showUpload && (
          <>
            <input ref={fileRef} type="file" className="hidden" onChange={onPick}
                   data-testid={`${testIdPrefix}-upload-input`} />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-7 text-xs"
              data-testid={`${testIdPrefix}-upload-btn`}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </>
        )}
      </div>

      {items === null ? (
        <div className="text-xs text-[#9CA3AF] italic" data-testid={`${testIdPrefix}-loading`}>Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-xs text-[#9CA3AF] italic" data-testid={`${testIdPrefix}-empty`}>
          No documents yet for {entityLabel}.
        </div>
      ) : compact ? (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-list`}>
          {list.map((a) => {
            const Icon = iconFor(a.content_type || a.mime);
            const fromCb = a.source_ref === "callback_request";
            return (
              <button type="button" key={a.id}
                onClick={() => setPreviewAtt(a)}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs max-w-[240px] ${fromCb ? "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100" : "border-[#E5E7EB] bg-white hover:bg-[#F5FBFC] text-[#1F2937]"}`}
                title={a.filename + (fromCb ? " (from callback)" : "")}
                data-testid={`${testIdPrefix}-chip-${a.id}`}>
                <Icon className={`w-3.5 h-3.5 shrink-0 ${fromCb ? "text-amber-700" : "text-[#0B7E8C]"}`} />
                <span className="truncate">{a.filename}</span>
                {fromCb && <span className="text-[9px] uppercase tracking-wider font-semibold bg-amber-200 text-amber-900 rounded px-1 py-[1px]">callback</span>}
                <span className="text-[10px] text-[#9CA3AF] shrink-0">{humanSize(a.size_bytes || a.size)}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <ul className="divide-y divide-[#E5E7EB] rounded-md border border-[#E5E7EB] bg-white"
            data-testid={`${testIdPrefix}-list`}>
          {list.map((a) => {
            const Icon = iconFor(a.content_type || a.mime);
            const delUrl = deleteUrlFor?.(a);
            const dlSrc = withAuth(fullUrl(a.download_url || (a.url ? null : `/api/uploads/${a.id}/download`)));
            const fromCb = a.source_ref === "callback_request";
            return (
              <li key={a.id} className={`flex items-center gap-3 px-3 py-2 ${fromCb ? "bg-amber-50/40" : ""}`}
                  data-testid={`${testIdPrefix}-row-${a.id}`}>
                <button type="button" onClick={() => setPreviewAtt(a)}
                  className="flex items-center gap-2 min-w-0 flex-1 text-left"
                  data-testid={`${testIdPrefix}-preview-${a.id}`}>
                  <Icon className={`w-4 h-4 shrink-0 ${fromCb ? "text-amber-700" : "text-[#0B7E8C]"}`} />
                  <div className="min-w-0">
                    <div className="text-sm text-[#1F2937] truncate flex items-center gap-1.5">
                      {a.filename}
                      {fromCb && (
                        <span className="text-[9px] uppercase tracking-wider font-semibold bg-amber-200 text-amber-900 rounded px-1 py-[1px]"
                              data-testid={`${testIdPrefix}-cb-badge-${a.id}`}>from callback</span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#9CA3AF] truncate">
                      {(a.content_type || a.mime || "").split("/").pop() || "file"} · {humanSize(a.size_bytes || a.size)}
                      {a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ""}
                      {a.uploaded_by_kind ? ` · ${a.uploaded_by_kind === "patient" ? "patient (mobile)" : "staff"}` : ""}
                    </div>
                  </div>
                </button>
                {dlSrc && (
                  <a href={dlSrc} target="_blank" rel="noreferrer"
                    className="text-[#0B7E8C] hover:text-[#128C97]"
                    title="Download"
                    data-testid={`${testIdPrefix}-download-${a.id}`}
                    onClick={(e) => e.stopPropagation()}>
                    <Download className="w-4 h-4" />
                  </a>
                )}
                {delUrl && !fromCb && (
                  <button type="button" onClick={() => onDelete(a)} disabled={deletingId === a.id}
                    className="text-red-600 hover:text-red-700 disabled:opacity-50"
                    title="Delete"
                    data-testid={`${testIdPrefix}-delete-${a.id}`}>
                    {deletingId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <PreviewModal open={!!previewAtt} onClose={() => setPreviewAtt(null)} att={previewAtt} />
    </div>
  );
}
